import {
  Volume2,
  Zap,
  Calendar,
  ShieldCheck,
  Phone,
  MessageSquare,
  Headphones,
} from "lucide-react";

function InstagramIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

export default function PlatformSection() {
  const capabilities = [
    {
      icon: Volume2,
      title: "A voice that sounds human",
      description:
        "Tone, speed and rhythm change as the call goes, so conversations feel natural.",
    },
    {
      icon: Zap,
      title: "Runs sales tasks on its own",
      description:
        "Outbound calls, cold calling, inbound leads and multi-step follow-ups happen without manual work.",
    },
    {
      icon: Calendar,
      title: "Qualifies and books meetings",
      description:
        "Qualification calls and appointment booking run automatically, and meetings go straight to your calendar.",
    },
    {
      icon: ShieldCheck,
      title: "Built for business use",
      description:
        "Encrypted phone lines, flexible setup, and you always own your data.",
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-white border-b border-slate-200/80">
      <div className="w-full max-w-[1200px] mx-auto px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: Feature List */}
          <div className="lg:col-span-6 space-y-10">
            <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-extrabold text-slate-900 leading-[1.15]">
              Everything your sales
              <br />
              calls need, in one platform
            </h2>

            <div className="space-y-7">
              {capabilities.map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <div key={idx} className="flex items-start gap-5">
                    <div className="w-11 h-11 rounded-xl bg-[#FEF3C7] border border-[#FDE68A]/60 flex items-center justify-center shrink-0 text-[#D97706]">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom note */}
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed pt-2">
              Also included: a customer care agent for support calls and
              callbacks, and an AI voice presenter that delivers your slide
              decks with narration and answers questions live.
            </p>
          </div>

          {/* Right Column: Integration Topology Diagram */}
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-8 sm:p-10 shadow-xs relative min-h-[380px] flex items-center justify-center overflow-hidden">
              {/* SVG Connecting Dashed Lines */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none stroke-slate-300"
                strokeDasharray="4 4"
                strokeWidth="1.5"
              >
                <line x1="25%" y1="28%" x2="50%" y2="50%" />
                <line x1="75%" y1="28%" x2="50%" y2="50%" />
                <line x1="25%" y1="72%" x2="50%" y2="50%" />
                <line x1="75%" y1="72%" x2="50%" y2="50%" />
              </svg>

              {/* Center Hub */}
              <div className="relative z-10 w-24 h-24 rounded-full bg-white border-2 border-[#F2A339] flex flex-col items-center justify-center shadow-xl">
                <img
                  src="https://echoleads.ai/assets/Echoleads%20Logo-02%203.png"
                  alt="EchoLeads Logo"
                  className="w-16 h-auto"
                />
              </div>

              {/* Satellite 1: Top-Left (Phone calls) */}
              <div className="absolute top-6 left-6 sm:top-8 sm:left-8 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-xs flex items-center gap-3">
                <Phone className="w-4 h-4 text-slate-700 shrink-0" />
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-slate-900">
                    Phone calls
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Inbound and outbound
                  </div>
                </div>
              </div>

              {/* Satellite 2: Top-Right (WhatsApp) */}
              <div className="absolute top-6 right-6 sm:top-8 sm:right-8 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-xs flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-slate-700 shrink-0" />
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-slate-900">
                    WhatsApp
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Reminders, confirmations
                  </div>
                </div>
              </div>

              {/* Satellite 3: Bottom-Left (Instagram) */}
              <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-xs flex items-center gap-3">
                <InstagramIcon className="w-4 h-4 text-slate-700 shrink-0" />
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-slate-900">
                    Instagram
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Replies in seconds
                  </div>
                </div>
              </div>

              {/* Satellite 4: Bottom-Right (Customer care) */}
              <div className="absolute bottom-6 right-6 sm:bottom-8 sm:right-8 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-xs flex items-center gap-3">
                <Headphones className="w-4 h-4 text-slate-700 shrink-0" />
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-slate-900">
                    Customer care
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Support and callbacks
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
